import { z } from "zod";

export const OSSchema = z.enum([
  "debian-12",
  "debian-11",
  "ubuntu-24.04",
  "ubuntu-22.04",
  "fedora-40",
  "fedora-39",
  "windows-11",
  "macos-15",
]);

export const InstanceTypeSchema = z.enum(["small", "medium", "large"]);

export const TechStackSchema = z.enum([
  "JavaScript",
  "TypeScript",
  "Node.js",
  "React",
  "Next.js",
  "Nuxt.js",
  "Vue.js",
  "Angular",
  "Remix",
  "Astro",
  "Nest.js",
  "WordPress",
  "Express.js",
  "Svelte",
  "Python",
  "Rust",
  "Go",
  "PHP",
  "Ruby",
  "Swift",
  "Kotlin",
  "Java",
  "C#",
  "C++",
]);

export const InstanceVolumeSizeSchema = z.number().int().min(5).max(100);

export const InstanceMetadataSchema = z.object({
  name: z.string().min(1).max(100),
  description: z.string().max(100).optional(),
  techStack: z.array(TechStackSchema),
});

export const RepositoryProviderSchema = z.enum(["github", "gitlab"]);

export const TransientRepositorySchema = z.object({
  repositoryId: z.string().min(1).max(100),
  gitAccount: z.object({
    id: z.number(),
    provider: RepositoryProviderSchema,
  }),
});

export type TransientRepositorySchemaType = z.infer<
  typeof TransientRepositorySchema
>;

export const InstanceRepositorySchema = z.object({
  repositories: TransientRepositorySchema.array().nonempty({
    message: "Please select at least one repository",
  }),
});

export const EnvironmentVariableSchema = z.object({
  id: z.number(),
  key: z
    .string()
    .min(1, {
      message: "Key must be at least 1 character",
    })
    .max(100),
  value: z
    .string()
    .min(1, {
      message: "Value must be at least 1 character",
    })
    .max(100),
});

export const InstanceRunConfigSchema = z.object({
  installCommand: z.string().min(1).max(200),
  devCommand: z.string().min(1).max(200),
  buildCommand: z.string().min(1).max(200),
  testCommand: z.string().min(1).max(200).optional(),
  environmentVariables: z.array(EnvironmentVariableSchema).refine(
    (envs) => {
      const keys = envs.map((env) => env.key);
      return keys.length === new Set(keys).size;
    },
    {
      message: "Environment variables must have unique keys",
    },
  ),
  os: OSSchema,
  instanceType: InstanceTypeSchema,
  instanceVolumeSize: InstanceVolumeSizeSchema,
});

export const InstanceCombinedEitherSchema = z.union([
  InstanceMetadataSchema,
  InstanceRepositorySchema,
  InstanceRunConfigSchema,
]);

export type InstanceCombinedEitherSchemaType = z.infer<
  typeof InstanceCombinedEitherSchema
>;

export const InstanceCombinedSchema = z.object({
  ...InstanceMetadataSchema.shape,
  ...InstanceRepositorySchema.shape,
  ...InstanceRunConfigSchema.shape,
});

export const InstanceCombinedSchemaPartial = InstanceCombinedSchema.partial();

export type InstanceCombinedSchemaType = z.infer<typeof InstanceCombinedSchema>;
